// homeView.js — THE HOME'S TABS: Main · Dashboard · Chat · Present · Build.
//
// Nika 2026-10-23: "instead of [fly,dashboard,agent] rename [Main, Dashboard,
// Chat, Present, Build] and draw related experience for each tab aligning AI
// mode, so therefore we might be able to remove dropdown from chatpromptarea
// on canvas and only keep in sidebar and other places".
//
// ONE TAB, ONE AI MODE (functions/assistantMode.js) — each tab is the screen
// its mode makes in the home's main area:
//   Main       'fly'           the Fly canvas (components/AssistantFlyView.js):
//                              the workspace's start screen, or the session's
//                              composed pages
//   Dashboard  'dashboard'     the home board (pages/main.js); an ask builds a
//                              generated board over it (AssistantBoardView)
//   Chat       'assistant'     the conversation in the main area (the main
//                              thread, components/AssistantMainThread.js);
//                              empty, it offers the workspace's starters
//                              (components/HomeTabStart.js, tab 'chat')
//   Present    'presentation'  a live deck (AssistantDeckView); before one,
//                              Present's start screen (HomeTabStart, 'present')
//   Build      'apps'          the app builder itself — the /add-app page on
//                              the home (components/HomeBuildView.js,
//                              functions/homeBuild.js; Nika 2026-10-23: "build
//                              tab should reuse ui/ux of /add-apps")
// Tools ('tools') has no tab of its own: it is a conversation, so its screen
// is Chat's. Every tab but Main asks with the MAIN placement
// (`assistantDock: 'main'` — the answer becomes the home's screen,
// functions/assistantMainThread.js assistantSendRoute); Fly holds the main area
// whatever the placement.
//
// THE TABS ARE THE MODE ON THE HOME, so the composer's mode dropdown stands
// down there (homeTabsOnScreen — canvas CanvasPromptTextarea's two chip
// mounts); the sidebar's chip and the composer's focus panel keep theirs.
//
// THE VIEW IS READ, NEVER STORED: the conversation holding the main area is
// Chat (Build in Apps mode), the Fly canvas is Main, a deck — or Presentation
// mode — is Present, Apps mode is Build, everything else is the dashboard. So
// the tabs agree with every other way in or out: the sidebar's mode menu, a
// `?fly=` link, a view's own Esc, a thread opened from the sidebar.
//
// MAIN IS THE DEFAULT VIEW (Nika 2026-10-22: "ok make fly default view"): a
// person who never chose a mode is in Fly mode (state-app.js), so their home
// opens on Main (functions/assistantFlyWorkspace.js
// assistantFlyWorkspaceMounted). A choice of their own — any tab here, any row
// of the mode menu — wins from then on, on every device (the record). Where
// the person cannot ask (assistantFlyAvailable), the home stays the dashboard
// and offers no tabs.
//
// FRANK: every export is self-contained; siblings through `this.call`.

// The tab on screen. Every gate is read up front (reactive callers).
export const homeViewOf = function homeViewOf(root) {
  const r = root && typeof root === 'object' ? root : {}
  const fns = (this.context && this.context.functions) || {}
  const thread =
    typeof fns.assistantMainThreadOpen === 'function'
      ? !!this.call('assistantMainThreadOpen', r)
      : false
  const fly = !!this.call('assistantFlyShown', r)
  const deck =
    typeof fns.assistantDeckShown === 'function' ? !!this.call('assistantDeckShown', r) : false
  const mode = r.assistantMode
  if (thread) return mode === 'apps' ? 'build' : 'chat'
  if (fly) return 'main'
  if (deck || mode === 'presentation') return 'present'
  if (mode === 'apps') return 'build'
  return 'dashboard'
}

// The tabs — AppSegmented's row contract (plain data; `text` is a bundle key;
// keys prefixed, so no state key names an HTML tag). Dashboard is the header's
// own word for the home board.
export const homeViewSegments = function homeViewSegments() {
  return [
    { key: 'viewMain', value: 'main', text: 'workspace.homeView.main' },
    { key: 'viewDashboard', value: 'dashboard', text: 'shell.headerTabs.dashboard' },
    { key: 'viewChat', value: 'chat', text: 'workspace.homeView.chat' },
    { key: 'viewPresent', value: 'present', text: 'workspace.homeView.present' },
    { key: 'viewBuild', value: 'build', text: 'workspace.homeView.build' }
  ]
}

// Three destinations, with the existing views kept as modes inside them.
// Derive the selected destination from the real view so links, the sidebar
// and Escape keep this navigation in sync without a second active-view state.
export const homeViewGroupOf = function homeViewGroupOf(root) {
  const view = this.call('homeViewOf', root)
  if (view === 'main' || view === 'chat') return 'ask'
  if (view === 'build' || view === 'present') return 'create'
  return 'home'
}

export const homeViewGroups = function homeViewGroups() {
  return [
    { key: 'groupHome', value: 'home', text: 'shell.app.home' },
    { key: 'groupAsk', value: 'ask', text: 'workspace.aiCap.ask' },
    { key: 'groupCreate', value: 'create', text: 'common.action.create' }
  ]
}

export const homeViewModes = function homeViewModes(root, destination) {
  const group = destination || this.call('homeViewGroupOf', root)
  if (group === 'create') {
    return [
      { key: 'modeBuild', value: 'build', text: 'ai.assistantMode.apps' },
      { key: 'modePresent', value: 'present', text: 'canvas.typeFilter.presentation' }
    ]
  }
  return [
    { key: 'modePages', value: 'main', text: 'canvas.navbar.pages.panelTitle' },
    { key: 'modeChat', value: 'chat', text: 'workspace.homeView.chat' }
  ]
}

export const homeViewGroupSelect = function homeViewGroupSelect(value, mode) {
  const root = this.state && this.state.root
  if (!root || typeof root.update !== 'function') return false
  if (!['home', 'ask', 'create'].includes(value)) return false
  const current = this.call('homeViewGroupOf', root)
  const requested =
    (value === 'ask' && ['main', 'chat'].includes(mode)) ||
    (value === 'create' && ['build', 'present'].includes(mode))
      ? mode
      : null
  if (value === current) return requested ? this.call('homeViewSelect', requested) : false
  const view = this.call('homeViewOf', root)
  // Only remember the mode, scoped to this workspace. Drafts, pages and
  // decks stay owned by their existing view state; no content is copied.
  const workspaceId = String(root.workspace?.id || root.workspace?._id || '')
  const stored = root._homeViewRecent
  const recent = stored?.workspaceId === workspaceId ? { ...stored } : { workspaceId }
  if (current === 'ask' || current === 'create') recent[current] = view
  root.update({ _homeViewRecent: recent })
  const next =
    value === 'home'
      ? 'dashboard'
      : value === 'ask'
        ? recent.ask === 'chat'
          ? 'chat'
          : 'main'
        : recent.create === 'present'
          ? 'present'
          : 'build'
  return this.call('homeViewSelect', requested || next)
}

// The shared dropdown owns placement, focus, dismissal and touch rows.
export const homeViewMenuOpen = function homeViewMenuOpen(anchor, group) {
  const root = this.state?.root
  if (!root || !['ask', 'create'].includes(group)) return false
  if (!this.call('homeViewShown', root)) return false
  const node = anchor?.node
  if (!node || typeof node.getBoundingClientRect !== 'function') return false
  const box = node.getBoundingClientRect()
  root.update(
    {
      contextDropdown: {
        open: true,
        x: Math.round(box.left),
        y: Math.round(box.bottom + 8),
        homeViewGroup: group,
        itemsFn: 'homeViewMenuItems',
        actionFn: 'homeViewMenuAction'
      }
    },
    { preventFetch: true }
  )
  return true
}

// The check names the view the next ask lands in — the ONE reader the thread
// head and the composer's placeholder read too (functions/assistantAskRoute.js
// assistantEffectiveView); a host without it reads the tab.
export const homeViewMenuItems = function homeViewMenuItems(root) {
  const menu = root?.contextDropdown
  if (!menu?.open || !['ask', 'create'].includes(menu.homeViewGroup)) return []
  const fns = (this.context && this.context.functions) || {}
  const tabOf = {
    pages: 'main',
    chat: 'chat',
    build: 'build',
    present: 'present',
    home: 'dashboard'
  }
  const current =
    typeof fns.assistantEffectiveView === 'function'
      ? tabOf[this.call('assistantEffectiveView', root)] || 'dashboard'
      : this.call('homeViewOf', root)
  return this.call('homeViewModes', root, menu.homeViewGroup).map((mode) => ({
    key: mode.key,
    action: 'homeViewMode',
    view: mode.value,
    label: mode.text,
    checked: current === mode.value
  }))
}

export const homeViewMenuAction = function homeViewMenuAction(action, item) {
  const root = this.state?.root
  if (!root || action !== 'homeViewMode' || !this.call('homeViewShown', root)) return false
  const group = root.contextDropdown?.homeViewGroup
  const offered = this.call('homeViewMenuItems', root)
  if (!offered.some((mode) => mode.view === item?.view)) return false
  root.update({ contextDropdown: null }, { preventFetch: true })
  this.call('homeViewGroupSelect', group, item.view)
  return true
}

// Horizontal arrows keep tab navigation; Down opens the selected tab's menu.
export const homeViewNavigationKeydown = function homeViewNavigationKeydown(event, item) {
  const group = item?.state?.value
  if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && group !== 'home') {
    event.preventDefault()
    this.call('homeViewMenuOpen', item, group)
    return
  }
  const track = item?.parent
  const items = typeof track?.getChildren === 'function' ? track.getChildren() : []
  const index = items.indexOf(item)
  if (index < 0) return
  const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key]
  const next = step
    ? items[(index + step + items.length) % items.length]
    : event.key === 'Home'
      ? items[0]
      : event.key === 'End'
        ? items[items.length - 1]
        : null
  if (!next || next === item) return
  event.preventDefault()
  this.call('homeViewGroupSelect', next.state?.value)
  next.node?.focus?.()
}

// Where the tabs are offered: the home of a workspace whose person can ask
// (functions/assistantFlyWorkspace.js assistantFlyAvailable: the assistant and
// its composer on) — never while the board is in edit mode (a tab would leave
// the edit half-done). Every key is read up front (reactive callers).
export const homeViewShown = function homeViewShown(root) {
  const r = root && typeof root === 'object' ? root : {}
  const editing = !!r._homeCustomizeMode
  const ws = r.workspace
  const workspaceId = (ws && (ws.id || ws._id)) || null
  const fns = (this.context && this.context.functions) || {}
  if (!workspaceId || editing) return false
  if (typeof fns.setAssistantMode !== 'function') return false
  if (typeof fns.assistantFlyAvailable === 'function') {
    return !!this.call('assistantFlyAvailable', r)
  }
  return true
}

// The tabs on screen: the workspace home itself (no pane over it — a sheet
// speaks for its own page), the tabs offered. While they are, the tab is the
// mode, and the composer's mode dropdown stands down (canvas
// CanvasPromptTextarea). Every key is read up front (reactive callers).
export const homeTabsOnScreen = function homeTabsOnScreen(root) {
  const r = root && typeof root === 'object' ? root : {}
  const fns = (this.context && this.context.functions) || {}
  const pane = !!(r._canvasSlot || r._canvasSlotSecond)
  const path = typeof r.currentPath === 'string' && r.currentPath ? r.currentPath : '/'
  const bare =
    typeof fns.stripWorkspacePrefix === 'function' ? this.call('stripWorkspacePrefix', path) : path
  const home = typeof bare === 'string' && bare.split(/[?#]/)[0].replace(/\/+$/, '') === ''
  const offered = !!this.call('homeViewShown', r)
  return home && !pane && offered
}

// The tab's choice (AppSegmented `select`: el.call(select, value)): its mode
// FOR THE HOME ONLY (`_homeMode`, never persisted — BLD-13), the main placement for every tab
// but Main, and the views that are not this tab's step aside (each stays in its
// own place — the thread in the sidebar and its chips, a board or a deck in the
// boards row).
export const homeViewSelect = function homeViewSelect(value) {
  const root = this.state && this.state.root
  if (!root || typeof root.update !== 'function') return false
  const tabs = ['main', 'dashboard', 'chat', 'present', 'build']
  if (!tabs.includes(value)) return false
  const fns = (this.context && this.context.functions) || {}
  const was = this.call('homeViewOf', root)
  if (was === value) return false
  // BLD-13 (Nika 2026-10-25: "Change only the home view") — a tab sets the
  // HOME's mode, never the person's pick: `_homeMode` names it for this
  // workspace (with the pick it covers, `barMode`), and setAssistantMode's
  // local write persists nothing. Off the home, homeModeSync gives the pick
  // back, so the build bar keeps the mode the person chose there.
  const mode = (m) => {
    if (root.assistantMode === m) return
    const workspaceId = String(root.workspace?.id || root.workspace?._id || '')
    const hm = root._homeMode
    const barMode =
      hm && hm.workspaceId === workspaceId && hm.barMode ? hm.barMode : root.assistantMode
    root.update({ _homeMode: { workspaceId, mode: m, barMode } }, { preventFetch: true })
    this.call('setAssistantMode', m, { local: true })
  }
  const placeMain = () => {
    if (root.assistantDock !== 'main' && typeof fns.setAssistantDock === 'function') {
      this.call('setAssistantDock', 'main')
    }
  }
  const away = (name) => {
    if (typeof fns[name] === 'function') this.call(name)
  }

  if (value === 'chat') {
    // A conversation mode first — the one the person had (Tools is one),
    // else the one Fly came from when it was one, else Assistant. Then the
    // main placement, and the thread last (a mode change may close a thread
    // on its way, assistantMode.js): the one going on, or an empty one with
    // the workspace's starters.
    // The person's own pick comes first when it is a conversation: the tabs
    // persist nothing (BLD-13), so the pick a home tab covers is in
    // `_homeMode.barMode`, else the record.
    const chat = ['assistant', 'tools']
    if (!chat.includes(root.assistantMode)) {
      const hm = root._homeMode
      const workspaceId = String(root.workspace?.id || root.workspace?._id || '')
      const own =
        hm && hm.workspaceId === workspaceId && hm.barMode
          ? hm.barMode
          : root.userPrefs && root.userPrefs.assistantMode
      const before = chat.includes(own)
        ? own
        : typeof fns.assistantModeBeforeFly === 'function'
          ? this.call('assistantModeBeforeFly')
          : 'assistant'
      mode(chat.includes(before) ? before : 'assistant')
    }
    placeMain()
    if (typeof fns.assistantMainThreadShow !== 'function') return false
    return !!this.call('assistantMainThreadShow')
  }

  // Every other tab: the conversation steps aside when it holds the home (it
  // stays in the sidebar and the thread chips) — Build before a build has none.
  if (
    (was === 'chat' || was === 'build') &&
    typeof fns.assistantMainThreadOpen === 'function' &&
    this.call('assistantMainThreadOpen', root)
  )
    away('assistantMainThreadClose')

  if (value === 'main') {
    // Not in Fly mode yet: the mode pick opens the canvas by itself (and puts
    // a board, a deck and the thread away — assistantFlyWorkspaceEnter).
    if (root.assistantMode !== 'fly') {
      mode('fly')
      return true
    }
    // Fly mode with its canvas closed (closed earlier, or left on another
    // route): bring the canvas back here.
    if (this.call('assistantFlyShown', root)) return true
    if (typeof fns.assistantFlyWorkspaceEnter === 'function') {
      return !!this.call('assistantFlyWorkspaceEnter')
    }
    return true
  }

  // Dashboard, Present, Build: the other tabs' views step aside — a deck for
  // the Dashboard and Build, a generated board for Present and Build — and a
  // Fly canvas shown outside Fly mode (a `?fly=` link answered in another
  // mode) is put away. Leaving Fly mode closes its canvas with the mode.
  if (value !== 'present') away('assistantDeckDismiss')
  if (value !== 'dashboard') away('assistantBoardDismiss')
  mode(value === 'dashboard' ? 'dashboard' : value === 'present' ? 'presentation' : 'apps')
  if (this.call('assistantFlyShown', root)) away('assistantFlyWorkspaceDismiss')
  placeMain()
  return true
}

// THE HOME'S MODE AND THE PERSON'S PICK (BLD-13). The always-mounted Fly
// picker (app.js AssistantFlyWorkspace_picker) calls this on workspace, mode,
// route and `_homeMode` changes, BEFORE assistantFlyWorkspaceMounted, so the
// canvas follows the mode this settles:
//   - on this workspace's home, the tab's mode is the live mode;
//   - anywhere else (another route, another workspace), the person's own pick
//     is: the record's `assistantMode`, else this device's, else the pick the
//     tab covered, else Fly (the default).
// Only a mode the tab itself set is ever given back — a live mode that is
// neither (the record landing, an explicit pick, which also clears
// `_homeMode`) is left alone. Plain root writes: nothing persists here.
export const homeModeSync = function homeModeSync() {
  const root = this.state && this.state.root
  if (!root || typeof root.update !== 'function') return false
  const hm = root._homeMode
  if (!hm || typeof hm !== 'object') return false
  const modes = ['assistant', 'dashboard', 'tools', 'apps', 'presentation', 'fly']
  const fns = (this.context && this.context.functions) || {}
  const workspaceId = String(root.workspace?.id || root.workspace?._id || '')
  const path = typeof root.currentPath === 'string' && root.currentPath ? root.currentPath : '/'
  const bare =
    typeof fns.stripWorkspacePrefix === 'function' ? this.call('stripWorkspacePrefix', path) : path
  const home = typeof bare === 'string' && bare.split(/[?#]/)[0].replace(/\/+$/, '') === ''
  const live = root.assistantMode
  if (home && hm.workspaceId === workspaceId) {
    if (!modes.includes(hm.mode) || live === hm.mode) return false
    root.update({ assistantMode: hm.mode }, { preventFetch: true })
    return true
  }
  const prefs = root.userPrefs && typeof root.userPrefs === 'object' ? root.userPrefs : null
  const stored =
    typeof fns.readStoredAssistantMode === 'function' ? this.call('readStoredAssistantMode') : null
  const pick =
    [prefs && prefs.assistantMode, stored, hm.barMode].find((m) => modes.includes(m)) || 'fly'
  const patch = {}
  if (hm.workspaceId !== workspaceId) patch._homeMode = null
  if (live === hm.mode && live !== pick) patch.assistantMode = pick
  if (!Object.keys(patch).length) return false
  root.update(patch, { preventFetch: true })
  return true
}
